/** Accès à la session courante depuis n’importe quel écran. */

import { useMutation } from '@tanstack/react-query'
import { use } from 'react'

import type { ChangerMotDePasse, RoleUtilisateur } from '@tb/shared'

import { api } from '../lib/api'

import { ContexteAuth, type ValeurAuth } from './contexte'
import { aAuMoinsRole, aRole } from './roles'
import type { AbonnePortail, Session, UtilisateurStaff } from './types'

export function useAuth(): ValeurAuth {
  const contexte = use(ContexteAuth)
  if (contexte === null) {
    throw new Error('useAuth doit être utilisé à l’intérieur de <AuthProvider>.')
  }
  return contexte
}

/** La session, ou `null` si personne n’est connecté. */
export function useSession(): Session | null {
  return useAuth().session
}

/** Le membre du personnel connecté, ou `null` (session abonné ou absente). */
export function useUtilisateurOuNull(): UtilisateurStaff | null {
  const session = useSession()
  return session?.sujet === 'staff' ? session.utilisateur : null
}

/**
 * Le membre du personnel connecté. À n’appeler que sous une garde
 * `<RequireStaff>` — sinon c’est un bug de routage, et il doit être bruyant.
 */
export function useUtilisateur(): UtilisateurStaff {
  const utilisateur = useUtilisateurOuNull()
  if (utilisateur === null) {
    throw new Error('Aucune session staff : cet écran doit être protégé par <RequireStaff>.')
  }
  return utilisateur
}

/** L’abonné connecté au portail. À n’appeler que sous `<RequireAbonne>`. */
export function useAbonne(): AbonnePortail {
  const session = useSession()
  if (session?.sujet !== 'abonne') {
    throw new Error('Aucune session abonné : cet écran doit être protégé par <RequireAbonne>.')
  }
  return session.personne
}

/** Rôle courant, ou `null` hors session staff. */
export function useRole(): RoleUtilisateur | null {
  return useUtilisateurOuNull()?.role ?? null
}

/** Hiérarchique : `useAAuMoinsRole('guichet')` est vrai pour un admin. */
export function useAAuMoinsRole(minimal: RoleUtilisateur): boolean {
  return aAuMoinsRole(useRole(), minimal)
}

/** Liste explicite : `useARole('admin', 'lecture')` exclut le guichet. */
export function useARole(...attendus: readonly RoleUtilisateur[]): boolean {
  return aRole(useRole(), ...attendus)
}

/* -------------------------------------------------------------------------- */
/* Changer son propre mot de passe                                            */
/* -------------------------------------------------------------------------- */

/**
 * ★ Le pendant, côté personnel, de la réinitialisation par un administrateur.
 *
 * Rien à invalider dans le cache : aucun écran n'affiche de mot de passe, et la
 * session courante reste ouverte (le serveur révoque les AUTRES). La réponse
 * est un `204` — elle ne porte rien, et surtout pas le secret.
 */
export function useChangerMonMotDePasse() {
  return useMutation({
    mutationFn: (corps: ChangerMotDePasse) =>
      api.put<void>('/auth/mot-de-passe', corps, { reponse: 'vide' }),
  })
}
